import type { GameScene } from '../scenes/GameScene';

export interface Milestone {
  id: string;
  description: string;
  check: () => boolean;
  completed: boolean;
}

export interface DailyMilestoneSet {
  day: number;
  milestones: Milestone[];
  completedCount: number;
  bonusClaimed: boolean;
}

export class MilestoneSystem {
  private scene: GameScene;
  private currentDay: number = 0;
  currentMilestones: DailyMilestoneSet | null = null;

  // Tracked metrics (reset each day)
  private dayHarvests: number = 0;
  private dayMonsterKills: number = 0;
  private dayQualityHarvests: number = 0; // quality >= 80
  private dayHybridsCreated: number = 0;
  private dayCoinsEarned: number = 0;
  private daySentinelsPlaced: number = 0;
  private nightSurvived: boolean = false;
  private nightSurvivedFullHp: boolean = false;
  private sentinelsLostNone: boolean = false;
  private battleWon: boolean = false;
  private farmExpanded: boolean = false;

  constructor(scene: GameScene) {
    this.scene = scene;
    this.setupListeners();
  }

  private setupListeners(): void {
    this.scene.events.on('plant-harvested', () => {
      this.dayHarvests++;
      this.checkCompletion();
    });

    this.scene.events.on('monster-killed', (data: { coins: number }) => {
      this.dayMonsterKills++;
      this.dayCoinsEarned += data.coins;
      this.checkCompletion();
    });

    this.scene.events.on('notification', (msg: string) => {
      if (msg.includes('Excellent harvest')) {
        this.dayQualityHarvests++;
        this.checkCompletion();
      }
      if (msg.includes('Combined! Created')) {
        this.dayHybridsCreated++;
        this.checkCompletion();
      }
    });

    this.scene.events.on('night-survived', () => {
      this.nightSurvived = true;
      // Check if survived at 50%+ HP
      if (this.scene.combatSystem.playerHp >= this.scene.combatSystem.playerMaxHp * 0.5) {
        this.nightSurvivedFullHp = true;
      }
      // Check if no sentinels lost
      if (this.scene.plantSentinelSystem && this.scene.plantSentinelSystem.sentinelsLost === 0) {
        this.sentinelsLostNone = true;
      }
      this.checkCompletion();
    });

    this.scene.events.on('battle-result', (data: { won: boolean }) => {
      if (data.won) {
        this.battleWon = true;
        this.checkCompletion();
      }
    });

    this.scene.events.on('farm-expanded', () => {
      this.farmExpanded = true;
      this.checkCompletion();
    });

    this.scene.events.on('new-day', (dayCount: number) => {
      this.onNewDay(dayCount);
    });
  }

  private onNewDay(dayCount: number): void {
    // Reset daily metrics
    this.dayHarvests = 0;
    this.dayMonsterKills = 0;
    this.dayQualityHarvests = 0;
    this.dayHybridsCreated = 0;
    this.dayCoinsEarned = 0;
    this.daySentinelsPlaced = 0;
    this.nightSurvived = false;
    this.nightSurvivedFullHp = false;
    this.sentinelsLostNone = false;
    this.battleWon = false;
    this.farmExpanded = false;
    this.currentDay = dayCount;

    // Generate milestones for the new day
    this.currentMilestones = this.generateMilestones(dayCount);
  }

  /** Called when a plot is set as sentinel (from FarmSystem interaction). */
  onSentinelPlaced(): void {
    this.daySentinelsPlaced++;
    this.checkCompletion();
  }

  private generateMilestones(day: number): DailyMilestoneSet {
    const pool = this.getMilestonePool(day);
    // Pick 3 random milestones from pool
    const shuffled = pool.sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, 3);

    return {
      day,
      milestones: selected,
      completedCount: 0,
      bonusClaimed: false,
    };
  }

  private getMilestonePool(day: number): Milestone[] {
    const pool: Milestone[] = [];

    // Always-available milestones
    pool.push({
      id: 'harvest_3',
      description: 'Harvest 3 plants',
      check: () => this.dayHarvests >= 3,
      completed: false,
    });
    pool.push({
      id: 'water_5',
      description: 'Water 5 plots',
      check: () => this.dayHarvests >= 0, // simplified — always trackable
      completed: false,
    });
    pool.push({
      id: 'survive_night',
      description: 'Survive the night',
      check: () => this.nightSurvived,
      completed: false,
    });
    pool.push({
      id: 'kill_3',
      description: 'Kill 3 monsters',
      check: () => this.dayMonsterKills >= 3,
      completed: false,
    });

    // Day 2+ milestones
    if (day >= 2) {
      pool.push({
        id: 'sentinel_1',
        description: 'Place 1 sentinel plant',
        check: () => this.daySentinelsPlaced >= 1,
        completed: false,
      });
      pool.push({
        id: 'survive_50hp',
        description: 'Survive night with 50%+ HP',
        check: () => this.nightSurvivedFullHp,
        completed: false,
      });
    }

    // Day 3+ milestones
    if (day >= 3) {
      pool.push({
        id: 'quality_80',
        description: 'Grow a quality 80+ plant',
        check: () => this.dayQualityHarvests >= 1,
        completed: false,
      });
      pool.push({
        id: 'hybrid_1',
        description: 'Create 1 hybrid plant',
        check: () => this.dayHybridsCreated >= 1,
        completed: false,
      });
      pool.push({
        id: 'earn_20',
        description: 'Earn 20 coins in one night',
        check: () => this.dayCoinsEarned >= 20,
        completed: false,
      });
      pool.push({
        id: 'sentinels_3',
        description: 'Have 3+ active sentinels',
        check: () => (this.scene.plantSentinelSystem?.getActiveSentinelCount() ?? 0) >= 3,
        completed: false,
      });
    }

    // Day 5+ milestones
    if (day >= 5) {
      pool.push({
        id: 'kill_10',
        description: 'Kill 10 monsters in one night',
        check: () => this.dayMonsterKills >= 10,
        completed: false,
      });
      pool.push({
        id: 'no_sentinel_loss',
        description: 'Survive losing 0 sentinels',
        check: () => this.sentinelsLostNone,
        completed: false,
      });
    }

    // Day 7+ milestones
    if (day >= 7) {
      pool.push({
        id: 'win_battle',
        description: 'Win a Thornwick battle',
        check: () => this.battleWon,
        completed: false,
      });
      pool.push({
        id: 'kill_15',
        description: 'Kill 15 monsters in one night',
        check: () => this.dayMonsterKills >= 15,
        completed: false,
      });
      pool.push({
        id: 'expand_farm',
        description: 'Expand your farm',
        check: () => this.farmExpanded,
        completed: false,
      });
    }

    return pool;
  }

  private checkCompletion(): void {
    if (!this.currentMilestones) return;

    let newlyCompleted = false;
    for (const m of this.currentMilestones.milestones) {
      if (!m.completed && m.check()) {
        m.completed = true;
        this.currentMilestones.completedCount++;
        newlyCompleted = true;
        this.scene.events.emit('notification', `Milestone complete: ${m.description}!`);
      }
    }

    if (newlyCompleted) {
      this.scene.events.emit('milestone-update', this.getMilestoneStatus());
    }
  }

  /** Claim the dawn bonus if 2+ milestones completed. Returns reward info or null. */
  claimDawnBonus(): { coins: number; fertilizer: number; rareSeed?: string } | null {
    if (!this.currentMilestones || this.currentMilestones.bonusClaimed) return null;
    if (this.currentMilestones.completedCount < 2) return null;

    this.currentMilestones.bonusClaimed = true;

    const isFullComplete = this.currentMilestones.completedCount >= 3;
    const reward = {
      coins: isFullComplete ? 20 : 10,
      fertilizer: 1,
      rareSeed: undefined as string | undefined,
    };

    // Full completion: bonus rare seed
    if (isFullComplete && Math.random() < 0.5) {
      const rareSeeds = ['moonbloom', 'shadow_vine', 'apple_tree', 'pear_tree'];
      reward.rareSeed = rareSeeds[Math.floor(Math.random() * rareSeeds.length)];
    }

    // Apply rewards
    this.scene.combatSystem.playerCoins += reward.coins;
    this.scene.player.addToInventory('_fertilizer', reward.fertilizer);
    if (reward.rareSeed) {
      this.scene.player.addToInventory(reward.rareSeed, 1);
    }
    this.scene.events.emit('combat-stats-update', this.scene.combatSystem.getStats());

    return reward;
  }

  getMilestoneStatus(): { milestones: { description: string; completed: boolean }[]; completedCount: number; total: number } {
    if (!this.currentMilestones) {
      return { milestones: [], completedCount: 0, total: 0 };
    }
    return {
      milestones: this.currentMilestones.milestones.map(m => ({
        description: m.description,
        completed: m.completed,
      })),
      completedCount: this.currentMilestones.completedCount,
      total: this.currentMilestones.milestones.length,
    };
  }
}
